import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"

// Hooks
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import CustomerTypeAddEdit from "./CustomerTypeAddEdit"
import { CustomerTypeHeader } from "./CustomerTypeHeader"
import PageWrapper from "components/Common/PageWrapper"
import { useCustomerTypes, useDeleteCustomerType } from "hooks/useCustomerType"

const CustomerTypeList = () => {
  document.title = "Customer Type List"

  // State
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [fetchedData, setFetchedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  // URL search params hook
  const {
    params,
    setPage,
    setSearch,
    resetParams,
    hasActiveFilters,
  } = useListParams({
    page: 1,
    search: "",
  })

  // Query params
  const queryParams = useMemo(() => {
    const {
      page = 1,
      per_page = 10,
      search,
    } = params

    const q = { page, per_page, search }

    q.search = search

    return q
  }, [
    params.page,
  ])

  // React Query hooks
  const {
    data: fetchedResponseData,
    isLoading,
    refetch: refetchResponseData,
    isRefetching,
  } = useCustomerTypes(queryParams)

  const { mutateAsync: deleteFetchedData, isPending: deleteLoading } =
    useDeleteCustomerType()

  // Toggle modal
  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setFetchedData(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setFetchedData(null)
    setIsEdit(false)
    toggleModal()
  }, [toggleModal])

  const handleEditClick = useCallback(
    item => {
      setFetchedData(item)
      setIsEdit(true)
      toggleModal()
    },
    [toggleModal]
  )

  const handleDeleteClick = useCallback(item => {
    setFetchedData(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!fetchedData?.id) return
    try {
      const res = await deleteFetchedData(fetchedData.id)
      toast.success(
        res?.message ?? "Customer Type deleted successfully."
      )
      setDeleteModal(false)
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete customer type.")
    }
  }, [fetchedData, deleteFetchedData])

  const handleRefresh = () => refetchResponseData()

  // Table columns
  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Slug",
        accessorKey: "slug",
      },
      {
        header: "Description",
        accessorKey: "description",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(row.original)}
                id={`edittooltip-${row.original.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`edittooltip-${row.original.id}`}
                >
                  Edit
                </UncontrolledTooltip>
              </Button>
            </li>
            <li>
              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(row.original)}
                id={`deletetooltip-${row.original.id}`}
              >
                <i className="mdi mdi-delete-outline" />
                <UncontrolledTooltip
                  placement="top"
                  target={`deletetooltip-${row.original.id}`}
                >
                  Delete
                </UncontrolledTooltip>
              </Button>
            </li>
          </ul>
        ),
      },
    ],
    [handleEditClick, handleDeleteClick]
  )

  const data = fetchedResponseData?.data?.data || []
  const paginationInfo = fetchedResponseData?.data

  return (
    <PageWrapper title="Type" breadcrumbItem="Type List">
      <CustomerTypeHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={handleRefresh}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3"></div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>
      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      {/* Modal for Add/Edit */}
      <CustomerTypeAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={fetchedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default CustomerTypeList
